import * as React from 'react'
import { useMemo } from 'react'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import Box from '@mui/material/Box'
import useTheme from '@mui/material/styles/useTheme'
import { alpha, Theme } from '@mui/material/styles'
import Card from '@mui/material/Card'
import {
  flattenMeasureForDisplay,
  getFormattedAddressFromLocation,
} from '../functions/functions'
import { PropertyRow } from '../PropertyRows'
import Typography from '@mui/material/Typography'
import { NoDetails } from '../NoDetails'
import AddressMapLink from '@user/features/MapView/AddressMapLink/AddressMapLink'
import { AdditionalDetails } from './AdditionalDetails'

type Props = {
  propertyRows: PropertyRow[] | null
  selectedLocation: LocationDTO
  address?: string
  entityId?: number
  entityType?: ENTITY_TYPES
}

const getStyles = (theme: Theme) => ({
  card: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    width: '100%',
    maxWidth: '848px',
    minWidth: '550px',
    height: '100%',
    lineHeight: '21px',
    margin: '0px auto',
    borderRadius: 0,
  },
})

export function HighlightsCard({
  selectedLocation,
  propertyRows,
  address,
  entityId,
  entityType,
}: Props) {
  const theme = useTheme()
  const styles = getStyles(theme)

  const selectedAddress = useMemo(
    () =>
      address ??
      (selectedLocation
        ? getFormattedAddressFromLocation(selectedLocation)
        : ''),
    [selectedLocation, address],
  )

  const additionalDetails = useMemo(
    () =>
      selectedLocation.propertyDetails
        ? flattenMeasureForDisplay(selectedLocation.propertyDetails)
        : [],
    [selectedLocation],
  )

  return (
    <Card elevation={1} sx={styles.card} variant="outlined">
      {Object.keys(additionalDetails ?? {}).length === 0 ? (
        <NoDetails />
      ) : (
        <>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h7">Highlights</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {(() => {
                const resolvedEntityId = entityId ?? selectedLocation?.entityId
                if (typeof resolvedEntityId === 'number') {
                  return (
                    <AddressMapLink
                      address={selectedAddress ?? ''}
                      serviceLocationId={selectedLocation?.id}
                      entityId={resolvedEntityId}
                      entityType={
                        entityType ??
                        (selectedLocation.entityType === 'Lead'
                          ? ENTITY_TYPES.Lead
                          : ENTITY_TYPES.Opportunity)
                      }
                      latitude={selectedLocation?.latitude}
                      longitude={selectedLocation?.longitude}
                    >
                      {selectedAddress}
                    </AddressMapLink>
                  )
                } else {
                  return <Typography>{selectedAddress}</Typography>
                }
              })()}
            </Box>
          </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            columnGap="14px" // spacing between columns
            rowGap="15px" // spacing between rows
            mt={2.4}
            sx={{ width: '100%' }}
          >
            {propertyRows?.map((item, index) => (
              <Box
                key={index}
                sx={{
                  width: '150px',
                  height: '98px',
                  bgcolor: alpha(theme.palette.tidalColors.purple[50], 0.3),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box>{item.icon}</Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {item.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <AdditionalDetails additionalDetails={additionalDetails} />
        </>
      )}
    </Card>
  )
}
